import React, { useCallback, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { useUpdateBundleOffer } from '~/hooks/apis/accommodation/useBundles';

import { BundleOffer } from '~/services/AccommodationService';

import { INCLUSION_ICONS } from '../inclusionIcons';

interface Props {
  bundleId: string;
  offer: BundleOffer;
  open: boolean;
  onClose: () => void;
}

function AccommodationBundleExperienceEditDialog({ bundleId, offer, open, onClose }: Props) {
  // Form state initialises from the `offer` prop. The parent conditionally renders this
  // dialog only when there's an offer to edit, so each open is a fresh mount and the
  // initializers re-run with the latest values. If the parent ever switches to always-mounted
  // (`open` toggled instead of presence-toggled), add a useEffect to sync these on offer change.
  const [experienceName, setExperienceName] = useState(offer.experienceName ?? '');
  const [experienceDescription, setExperienceDescription] = useState(offer.experienceDescription ?? '');
  const [icon, setIcon] = useState(offer.icon ?? '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateBundleOfferMutation = useUpdateBundleOffer();

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      try {
        await updateBundleOfferMutation.mutateAsync({
          bundleId,
          offerId: offer.id,
          payload: {
            experienceName: experienceName.trim() || undefined,
            experienceDescription: experienceDescription.trim() || undefined,
            icon: icon.trim() || undefined,
          },
        });
        onClose();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to update experience';
        setErrorMessage(msg);
      }
    },
    [updateBundleOfferMutation, bundleId, offer.id, experienceName, experienceDescription, icon, onClose],
  );

  const isSubmitting = updateBundleOfferMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Experience</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Experience Name"
              value={experienceName}
              onChange={(e) => setExperienceName(e.target.value)}
              fullWidth
              helperText="Headline shown above the bundle inclusion. Falls back to the bundle name if empty."
            />
            <TextField
              label="Experience Description"
              value={experienceDescription}
              onChange={(e) => setExperienceDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Body text shown beneath the headline on the offer page."
            />
            <Autocomplete
              freeSolo
              options={INCLUSION_ICONS}
              value={icon}
              onChange={(_event, value) => setIcon(value ?? '')}
              onInputChange={(_event, value) => setIcon(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Icon (optional)"
                  helperText="Type to search the customer-side icon list. Free-text accepted — unknown values render as no icon."
                />
              )}
              fullWidth
            />
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AccommodationBundleExperienceEditDialog;
